import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FeedDocument,
  Newsletter,
} from '../utils/feedStore';
import { getTrackForRole } from '../data/academyContent';
import { academyAPI, feedAPI, workstreamAPI } from '../services/api';
import vehicleIconImg   from '../assets/icons/vehicle.png';
import clipboardIconImg from '../assets/icons/clipboard.png';
import './MyFeed.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const CATEGORY_GRADIENTS: Record<string, string> = {
  'Cyber Security':      'linear-gradient(135deg, #3730a3, #4f46e5)',
  'Cost Management':     'linear-gradient(135deg, #b45309, #d97706)',
  'Professional Advice': 'linear-gradient(135deg, #0369a1, #0284c7)',
  'Policy Updates':      'linear-gradient(135deg, #047857, #059669)',
  'Compliance':          'linear-gradient(135deg, #7c3aed, #8b5cf6)',
  'Industry News':       'linear-gradient(135deg, #be123c, #e11d48)',
};
const DEFAULT_GRADIENT = 'linear-gradient(135deg, #001f3f, #003366)';

const DOC_CAT: Record<string, { label: string; color: string; bg: string }> = {
  'policy':     { label: 'Policy',     color: '#001f3f', bg: '#e0e8f5' },
  'claim-form': { label: 'Claim Form', color: '#92400e', bg: '#fef3c7' },
  'guide':      { label: 'Guide',      color: '#065f46', bg: '#d1fae5' },
  'other':      { label: 'Other',      color: '#374151', bg: '#f3f4f6' },
};

function fmtDate(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtFileSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// ── Training manual viewer modal ─────────────────────────────────────────────
const TrainingManualViewerModal: React.FC<{ role: string; onClose: () => void }> = ({ role, onClose }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const track = getTrackForRole(role);

  useEffect(() => {
    if (!track) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const modules = track.modules.map(m => ({ title: m.title, content: m.manualContent }));
        const res = await academyAPI.downloadTrainingManual(track.role, track.trackName, modules);
        if (!cancelled) {
          const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
          setBlobUrl(url);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [track]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = () => {
    if (!blobUrl || !track) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `Myshield-${track.trackName.replace(/\s+/g, '-')}-Training-Manual.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="myfeed-overlay" onClick={onClose}>
      <div className="myfeed-docviewer" onClick={e => e.stopPropagation()}>
        <div className="myfeed-docviewer-head">
          <div className="myfeed-docviewer-meta">
            <span className="myfeed-doc-tag" style={{ background: '#e0e8f5', color: '#001f3f', marginRight: 10 }}>Guide</span>
            <span className="myfeed-docviewer-name">
              {track ? `${track.trackName} — Training Manual` : 'Training Manual'}
            </span>
            <span className="myfeed-docviewer-desc">Your full role-specific Myshield training manual</span>
          </div>
          <div className="myfeed-docviewer-actions">
            <button className="btn btn-primary" onClick={handleDownload} disabled={!blobUrl}>
              ⬇ Download
            </button>
            <button className="myfeed-modal-close" onClick={onClose} style={{ position: 'static', width: 36, height: 36 }}>
              ✕
            </button>
          </div>
        </div>
        <div className="myfeed-docviewer-body">
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 12, color: '#9ca3af' }}>
              <div style={{ fontSize: 28 }}>📄</div>
              <div style={{ fontSize: 14 }}>Generating your training manual…</div>
            </div>
          )}
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#c53030', fontSize: 14 }}>
              Could not load the training manual. Please try again.
            </div>
          )}
          {!loading && !error && blobUrl && (
            <iframe src={blobUrl} title="Training Manual" className="myfeed-docviewer-iframe" />
          )}
        </div>
      </div>
    </div>
  );
};

// ── PDF document viewer modal ────────────────────────────────────────────────
const DocViewerModal: React.FC<{ doc: FeedDocument; onClose: () => void }> = ({ doc, onClose }) => {
  const [blobUrl, setBlobUrl] = useState('');
  const cat = DOC_CAT[doc.category];

  useEffect(() => {
    const byteArr = new Uint8Array(Array.from(atob(doc.fileData), c => c.charCodeAt(0)));
    const blob = new Blob([byteArr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [doc.fileData]);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = doc.filename || `${doc.name}.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="myfeed-overlay" onClick={onClose}>
      <div className="myfeed-docviewer" onClick={e => e.stopPropagation()}>
        <div className="myfeed-docviewer-head">
          <div className="myfeed-docviewer-meta">
            <span className="myfeed-doc-tag" style={{ background: cat.bg, color: cat.color, marginRight: 10 }}>
              {cat.label}
            </span>
            <span className="myfeed-docviewer-name">{doc.name}</span>
            {doc.description && (
              <span className="myfeed-docviewer-desc">{doc.description}</span>
            )}
          </div>
          <div className="myfeed-docviewer-actions">
            <button className="btn btn-primary" onClick={handleDownload} disabled={!blobUrl}>
              ⬇ Download
            </button>
            <button className="myfeed-modal-close" onClick={onClose} style={{ position: 'static', width: 36, height: 36 }}>
              ✕
            </button>
          </div>
        </div>
        <div className="myfeed-docviewer-body">
          {blobUrl
            ? <iframe src={blobUrl} title={doc.name} className="myfeed-docviewer-iframe" />
            : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>Loading…</div>
          }
        </div>
      </div>
    </div>
  );
};

// ── Newsletter reader modal ───────────────────────────────────────────────────
const NewsletterModal: React.FC<{ nl: Newsletter; onClose: () => void }> = ({ nl, onClose }) => {
  const gradient = CATEGORY_GRADIENTS[nl.category] ?? DEFAULT_GRADIENT;
  const hasImg = !!nl.headerImage;
  return (
    <div className="myfeed-overlay" onClick={onClose}>
      <div className="myfeed-modal myfeed-modal--wide" onClick={e => e.stopPropagation()}>
        <div
          className="myfeed-modal-head"
          style={{
            minHeight: 130,
            ...(hasImg
              ? { backgroundImage: `url(${nl.headerImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : { background: gradient }),
          }}
        >
          {hasImg && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />}
          <button className="myfeed-modal-close" onClick={onClose} style={{ zIndex: 2 }}>✕</button>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, fontWeight: 700, letterSpacing: 1.8, textTransform: 'uppercase' as const, marginBottom: 10 }}>
              {nl.category} · {nl.readTime} min read · {fmtDate(nl.publishedAt)}
            </div>
            <h3 className="myfeed-modal-title" style={{ fontSize: 22 }}>{nl.title}</h3>
          </div>
        </div>
        <div className="myfeed-modal-content myfeed-nl-body">
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={{ __html: nl.body }} />
        </div>
        <div style={{ padding: '0 34px 28px', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const MyFeed: React.FC = () => {
  const { user, permissions } = useAuth();
  const navigate = useNavigate();

  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [documents, setDocuments]     = useState<FeedDocument[]>([]);
  const [selectedNl, setSelNl]        = useState<Newsletter | null>(null);
  const [selectedDoc, setSelDoc]      = useState<FeedDocument | null>(null);
  const [showTrainingManual, setShowTrainingManual] = useState(false);
  const [workstreamCount, setWorkstreamCount] = useState<number | null>(null);

  // Determine if the current user has a training track
  const userTrack = user ? getTrackForRole(user.role) : null;

  const loadData = useCallback(async () => {
    try {
      const [nRes, dRes] = await Promise.all([
        feedAPI.getNewsletters(),
        feedAPI.getDocuments(),
      ]);
      setNewsletters(nRes.data);
      setDocuments(dRes.data);
    } catch {
      // silently ignore — no content shown on error
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!user || user.role === 'Client') return;
    workstreamAPI.getPendingCount()
      .then(r => setWorkstreamCount(r.data.total ?? 0))
      .catch(() => setWorkstreamCount(null));
  }, [user]);

const handleDocOpen = (doc: FeedDocument) => {
    if (doc.url) {
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    } else if (doc.fileData) {
      setSelDoc(doc);
    }
  };

  // Only show documents that actually open somewhere — an uploaded PDF or an
  // external link. Seeded placeholders with neither (e.g. a guide awaiting its
  // PDF upload) are hidden until content is added via Manage Feed.
  const visibleDocuments = documents.filter(d => !!d.url || !!d.fileData);

  return (
    <div className="myfeed-wrap">

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <div className="myfeed-hero">
        <div className="myfeed-hero-content">
          <p className="myfeed-hero-eyebrow">{getGreeting()}</p>
          <h1 className="myfeed-hero-name">
            {user?.firstName} {user?.lastName}
            {user?.clientName && (
              <span className="myfeed-hero-company"> · {user.clientName}</span>
            )}
          </h1>
          <p className="myfeed-hero-sub">
            Your personalised Yourshield updates, resources and documents — all in one place.
          </p>
        </div>
        {permissions.canViewAdminTools && (
          <button className="myfeed-manage-btn" onClick={() => navigate('/admin-tools')}>
            ⚙ Manage Feed
          </button>
        )}
      </div>

      {user?.role !== 'Client' && workstreamCount != null && (
        <div
          className={`myfeed-workstream-pulse ${
            workstreamCount <= 5
              ? 'myfeed-workstream-pulse--green'
              : workstreamCount < 10
                ? 'myfeed-workstream-pulse--amber'
                : 'myfeed-workstream-pulse--red'
          }`}
          onClick={() => navigate('/workstream')}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && navigate('/workstream')}
        >
          <div className="myfeed-workstream-pulse-left">
            <span className="myfeed-workstream-pulse-orb" />
            <div>
              <div className="myfeed-workstream-pulse-title">Workstream</div>
              <div className="myfeed-workstream-pulse-sub">
                {workstreamCount === 0
                  ? 'No pending workstream items'
                  : `${workstreamCount} item${workstreamCount === 1 ? '' : 's'} waiting for review`}
              </div>
            </div>
          </div>
          <div className="myfeed-workstream-pulse-count">{workstreamCount}</div>
        </div>
      )}

      {/* ── Fleet quick link ─────────────────────────────────────────────────── */}
      <div
        className="myfeed-fleet-link"
        onClick={() => navigate('/vehicles')}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && navigate('/vehicles')}
      >
        <div className="myfeed-fleet-link-left">
          <img src={vehicleIconImg} alt="" className="myfeed-fleet-link-icon" />
          <div>
            <div className="myfeed-fleet-link-title">{user?.role === 'Client' ? 'My Fleet' : 'All Fleets'}</div>
            <div className="myfeed-fleet-link-sub">Vehicles, named drivers &amp; sites</div>
          </div>
        </div>
        <span className="myfeed-fleet-link-arrow">&#8594;</span>
      </div>

      {/* ── Yourshield Updates (newsletters) ─────────────────────────────────── */}
      <section className="myfeed-section">
        <div className="myfeed-section-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 className="myfeed-section-title">Yourshield Updates</h2>
            {newsletters.length > 0 && (
              <span className="myfeed-count-badge">{newsletters.length}</span>
            )}
          </div>
          <span style={{ fontSize: 13, color: '#9ca3af' }}>
            Newsletters & industry insights for your team
          </span>
        </div>

        {newsletters.length === 0 ? (
          <div className="myfeed-empty"><p>No newsletters yet — check back soon.</p></div>
        ) : (
          <div className="myfeed-nl-grid">
            {newsletters.map((nl, i) => {
              const gradient = CATEGORY_GRADIENTS[nl.category] ?? DEFAULT_GRADIENT;
              const featured = i === 0;
              const fullWidth = i === 2;
              return (
                <div
                  key={nl.id}
                  className={`myfeed-nl-card${featured ? ' myfeed-nl-card--featured' : ''}${fullWidth ? ' myfeed-nl-card--full' : ''}`}
                  onClick={() => setSelNl(nl)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && setSelNl(nl)}
                >
                <div
                    className="myfeed-nl-card-hdr"
                    style={nl.headerImage
                      ? { backgroundImage: `url(${nl.headerImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                      : { background: gradient }
                    }
                  >
                    {nl.headerImage && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.42)' }} />
                    )}
                    <span className="myfeed-nl-readtime" style={{ position: 'relative', zIndex: 1 }}>{nl.readTime} min read</span>
                  </div>
                  <div className="myfeed-nl-card-body">
                    <div className="myfeed-nl-category">{nl.category}</div>
                    <h3 className="myfeed-nl-title">{nl.title}</h3>
                    <p className="myfeed-nl-excerpt">{nl.excerpt}</p>
                    <div className="myfeed-nl-footer">
                      <span className="myfeed-nl-date">{fmtDate(nl.publishedAt)}</span>
                      <button className="myfeed-nl-read-btn" onClick={e => { e.stopPropagation(); setSelNl(nl); }}>
                        Read More →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Useful Links (training manual + documents) ────────────────────────── */}
      <section className="myfeed-section">
        <div className="myfeed-section-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 className="myfeed-section-title">Useful Links</h2>
            {(visibleDocuments.length > 0 || userTrack) && (
              <span className="myfeed-count-badge">
                {visibleDocuments.length + (userTrack ? 1 : 0)}
              </span>
            )}
          </div>
        </div>

        <div className="myfeed-docs-grid">
          {/* Training manual tile — always first when the user has a track */}
          {userTrack && (
            <div
              className="myfeed-doc-card myfeed-doc-card--guide myfeed-doc-card--clickable"
              onClick={() => setShowTrainingManual(true)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setShowTrainingManual(true)}
            >
              <div className="myfeed-doc-icon" style={{ background: '#d1fae5' }}>
                <span style={{ fontSize: 22 }}>🎓</span>
              </div>
              <div className="myfeed-doc-name">{userTrack.trackName} — Training Manual</div>
              <div className="myfeed-doc-desc">
                Your full role-specific guide to using the Myshield portal, compliance obligations, and step-by-step instructions.
              </div>
              <div className="myfeed-doc-footer">
                <span className="myfeed-doc-tag" style={{ background: '#d1fae5', color: '#065f46' }}>Guide</span>
                <span className="myfeed-doc-view-hint">View Manual</span>
              </div>
            </div>
          )}

          {/* Document / link tiles */}
          {visibleDocuments.map(doc => {
            const cat = DOC_CAT[doc.category];
            const isLink = !!doc.url;
            return (
              <div
                key={doc.id}
                className={`myfeed-doc-card myfeed-doc-card--${doc.category} myfeed-doc-card--clickable`}
                onClick={() => handleDocOpen(doc)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && handleDocOpen(doc)}
              >
                <div className="myfeed-doc-icon" style={{ background: cat.bg }}>
                  {isLink
                    ? <span style={{ fontSize: 22 }}>🔗</span>
                    : <img src={clipboardIconImg} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  }
                </div>
                <div className="myfeed-doc-name">{doc.name}</div>
                <div className="myfeed-doc-desc">{doc.description}</div>
                <div className="myfeed-doc-footer">
                  <span className="myfeed-doc-tag" style={{ background: cat.bg, color: cat.color }}>
                    {cat.label}
                  </span>
                  <span className="myfeed-doc-view-hint">
                    {isLink ? 'Visit Link →' : `View${doc.fileSize ? ` · ${fmtFileSize(doc.fileSize)}` : ''}`}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Empty state — only shown when neither the training tile nor any docs are visible */}
          {!userTrack && visibleDocuments.length === 0 && (
            <p style={{ color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>No links in this category yet.</p>
          )}
        </div>
      </section>

      {/* ── Training manual viewer modal ─────────────────────────────────────── */}
      {showTrainingManual && user && (
        <TrainingManualViewerModal role={user.role} onClose={() => setShowTrainingManual(false)} />
      )}

      {/* ── PDF document viewer modal ───────────────────────────────────────── */}
      {selectedDoc && (
        <DocViewerModal doc={selectedDoc} onClose={() => setSelDoc(null)} />
      )}

      {/* ── Newsletter reader modal ─────────────────────────────────────────── */}
      {selectedNl && (
        <NewsletterModal nl={selectedNl} onClose={() => setSelNl(null)} />
      )}
    </div>
  );
};

export default MyFeed;
